import { Ticket } from '../types';

export type TecFormCategory = 'traffic_enforcement_order' | 'penalty_charge_notice';
export type TecFormId = 'te7' | 'te9' | 'pe2' | 'pe3';

export interface TecFormRecommendation {
  category: TecFormCategory;
  categoryLabel: string;
  primaryForm: string;
  outOfTimeForm: string;
  primaryFormId: TecFormId;
  outOfTimeFormId: TecFormId;
  primaryLink: string;
  outOfTimeLink: string;
  guidanceLink?: string;
  isOutOfTime: boolean;
  matchedKeywords: string[];
}

const TEC_FORM_DEADLINE_DAYS = 21;

const ROAD_USER_CHARGE_KEYWORDS = [
  'dart charge',
  'dartford',
  'mersey gateway',
  'mersey',
  'clean air',
  'caz',
  'ulez',
  'lez',
  'low emission',
  'emission',
  'congestion charge',
  'road user',
  'toll',
  'crossing',
  'tunnel'
];

const ORDER_FOR_RECOVERY_KEYWORDS = [
  'order for recovery',
  'order for recovery of unpaid penalty charge',
  'form te3',
  'te3'
];

const normalizeText = (value?: string | null): string => (value || '').toLowerCase();

const buildCorpus = (ticket: Ticket): string => {
  const phrases = ticket.key_phrases || [];
  return [
    ticket.contravention_type,
    ticket.issuing_authority,
    ticket.location,
    ticket.document_intent,
    ticket.document_type,
    ticket.stage,
    ...phrases
  ]
    .map(normalizeText)
    .join(' ');
};

const extractRoadUserMatches = (ticket: Ticket): string[] => {
  const corpus = buildCorpus(ticket);

  return ROAD_USER_CHARGE_KEYWORDS.filter((keyword) => corpus.includes(keyword));
};

const extractOrderForRecoveryMatches = (ticket: Ticket): string[] => {
  const corpus = buildCorpus(ticket);

  return ORDER_FOR_RECOVERY_KEYWORDS.filter((keyword) => corpus.includes(keyword));
};

const getDaysSinceIssue = (ticket: Ticket, now: Date): number | null => {
  const issueDate = new Date(ticket.issue_date);
  if (Number.isNaN(issueDate.getTime())) return null;
  return Math.floor((now.getTime() - issueDate.getTime()) / (1000 * 3600 * 24));
};

export const getTecFormRecommendation = (ticket: Ticket, now: Date = new Date()): TecFormRecommendation => {
  const roadUserMatches = extractRoadUserMatches(ticket);
  const orderForRecoveryMatches = extractOrderForRecoveryMatches(ticket);
  const stageText = normalizeText(ticket.stage);
  const isOrderForRecoveryStage = stageText.includes('order for recovery') || stageText.includes('te3') || stageText.includes('ofr');
  const isOrderForRecovery = isOrderForRecoveryStage || ticket.document_type === 'ORDER_FOR_RECOVERY' || orderForRecoveryMatches.length > 0;
  const matchedKeywords = Array.from(
    new Set([
      ...(ticket.document_type === 'ORDER_FOR_RECOVERY' ? ['order for recovery'] : []),
      ...(isOrderForRecoveryStage ? ['order for recovery'] : []),
      ...orderForRecoveryMatches,
      ...roadUserMatches
    ])
  );
  const isRoadUserCharge = roadUserMatches.length > 0;
  const daysSinceIssue = getDaysSinceIssue(ticket, now);
  const isOutOfTime = typeof daysSinceIssue === 'number' ? daysSinceIssue > TEC_FORM_DEADLINE_DAYS : false;

  if (isOrderForRecovery || isRoadUserCharge) {
    return {
      category: 'traffic_enforcement_order',
      categoryLabel: isOrderForRecovery
        ? 'Order for Recovery (TE3)'
        : 'Road user charge (Dart/Mersey/CAZ/ULEZ/LEZ)',
      primaryForm: 'TE9 (Witness Statement)',
      outOfTimeForm: 'TE7 (Out of Time Application)',
      primaryFormId: 'te9',
      outOfTimeFormId: 'te7',
      primaryLink: '/TE9.pdf',
      outOfTimeLink: '/TE7.pdf',
      isOutOfTime,
      matchedKeywords
    };
  }

  return {
    category: 'penalty_charge_notice',
    categoryLabel: 'Penalty Charge Notice (parking/bus lane/moving traffic)',
    primaryForm: 'PE3 (Statutory Declaration)',
    outOfTimeForm: 'PE2 (Out of Time Application)',
    primaryFormId: 'pe3',
    outOfTimeFormId: 'pe2',
    primaryLink: '/PE3.pdf',
    outOfTimeLink: '/form-pe2-eng.pdf',
    guidanceLink: 'https://assets.publishing.service.gov.uk/media/6037706ee90e070558e4299b/pe3-guidance-eng.pdf',
    isOutOfTime,
    matchedKeywords
  };
};
